// components/AI/useChatImages.js
// [VISION] Seluruh mesin lampiran gambar untuk chat — dipisah dari AIChat.js.
//
// Ini SATU-SATUNYA tempat logika lampiran hidup: state, validasi, kompresi,
// paste, dan drag-drop. AIChat.js hanya memanggil hook ini dan memasang
// handler-nya; tidak ada lagi cabang `isCode` yang tersebar untuk urusan
// gambar.
//
// Kenapa hook, bukan komponen terpisah: lampiran perlu berbagi state dengan
// alur kirim (`sendMessage` harus membaca `attachedImgs` dan mengosongkannya
// setelah request BERHASIL). Sebagai komponen, state itu harus diangkat lagi
// ke induk — jadi tidak ada yang benar-benar terpisah.
//
// PENTING — hook TIDAK BOLEH dipanggil bersyarat (aturan React). Jadi hook ini
// selalu dipanggil, dan `enabled` yang menentukan apakah handler bereaksi.
// Saat `enabled === false` semua handler no-op dan lampiran selalu kosong.
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  isImageFile, imageFileToDataUri, dataUriToImagePart,
  b64Bytes, IMG_MAX_BYTES, makeThumbDataUri, makePreviewDataUri,
} from '../../utils/imageAttach';

// Batas jumlah dipasang di klien agar user tahu lebih awal; AI Service tetap
// menegakkan batas yang sama (satu sumber kebenaran ada di sana).
// HARUS <= AI_VISION_MAX_IMAGES di ai-service (default 4). Kalau angka di sini
// dinaikkan tanpa menaikkan env di server, kelebihannya ditolak 400.
export const CHAT_IMG_MAX = 8;

export function useChatImages({ enabled, t }) {
  const [attachedImgs, setAttachedImgs] = useState([]);   // [{ id, uri, name, bytes }]
  const [imgBusy, setImgBusy] = useState(false);
  const [imgOver, setImgOver] = useState(false);          // highlight saat file di-drag
  const imgInputRef = useRef(null);

  // id unik yang tidak bergantung pada Date.now(): dua batch dalam milidetik
  // yang sama pernah bisa bertabrakan dan membuat React salah rekonsiliasi.
  const seqRef = useRef(0);

  // Berpindah keluar dari mode Coding harus membuang lampiran — kalau tidak,
  // gambar ikut "menempel" saat user kembali dan bisa terkirim tanpa disadari.
  useEffect(() => {
    if (!enabled) { setAttachedImgs([]); setImgOver(false); }
  }, [enabled]);

  const addImages = useCallback(async (fileList) => {
    if (!enabled) return;
    const files = Array.from(fileList || []).filter(isImageFile);
    if (!files.length) return;
    if (files.some(f => f.size > IMG_MAX_BYTES)) {
      window.alert(t('aiChat.imageTooLarge'));
      return;
    }
    setImgBusy(true);
    try {
      const added = [];
      for (const f of files) {
        const uri = await imageFileToDataUri(f);
        const part = dataUriToImagePart(uri);
        // Format di luar png/jpeg/gif/webp tidak diterima Converse.
        if (!part) { window.alert(t('aiChat.imageUnsupported', { name: f.name || '' })); continue; }
        seqRef.current += 1;
        // [THUMB] Dibuat SEKARANG, bukan saat kirim: di sini kita masih punya
        // data URI penuh dan user sedang menunggu (imgBusy sudah aktif), jadi
        // biayanya tidak terasa. Menundanya ke sendMessage akan menambah jeda
        // tepat pada aksi yang paling ingin terasa cepat.
        // Dua turunan sekaligus, satu kali decode masing-masing: miniatur untuk
        // daftar, preview 640px untuk lightbox saat diklik.
        const thumb   = await makeThumbDataUri(uri);
        const preview = await makePreviewDataUri(uri);
        added.push({ id: `img-${seqRef.current}`, uri, thumb, preview, name: f.name || 'image', bytes: b64Bytes(part.b64) });
      }
      if (added.length) {
        setAttachedImgs(prev => {
          const next = [...prev, ...added].slice(0, CHAT_IMG_MAX);
          if (prev.length + added.length > CHAT_IMG_MAX) window.alert(t('aiChat.imageMaxCount', { max: CHAT_IMG_MAX }));
          return next;
        });
      }
    } catch (err) {
      window.alert(err?.message === 'IMG_DECODE_FAILED'
        ? t('aiChat.imageUnsupported', { name: err.fileName || '' })
        : (err?.message || t('aiChat.imageFailed')));
    } finally { setImgBusy(false); }
  }, [enabled, t]);

  const removeImage = useCallback((id) => {
    setAttachedImgs(prev => prev.filter(a => a.id !== id));
  }, []);

  const clearImages = useCallback(() => setAttachedImgs([]), []);

  const openPicker = useCallback(() => imgInputRef.current?.click(), []);

  // onChange untuk <input type="file">.
  // PENTING: salin dulu jadi array. `e.target.files` adalah referensi LIVE ke
  // FileList milik input — begitu value di-reset (agar file yang SAMA bisa
  // dipilih ulang), list yang sama ikut kosong dan handler tak pernah
  // terpanggil. Gagalnya SENYAP: tanpa alert, tanpa error console.
  const onFileSelected = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length) addImages(files);
  }, [addImages]);

  // Paste screenshot: tidak perlu simpan ke file dulu. Paste berulang MENUMPUK
  // sehingga runtutan langkah bisa dikirim dalam satu pertanyaan, urut sesuai
  // urutan paste.
  const onPaste = useCallback((e) => {
    if (!enabled) return;
    const imgs = Array.from(e.clipboardData?.items || [])
      .filter(it => it.kind === 'file' && /^image\//i.test(it.type))
      .map(it => it.getAsFile())
      .filter(Boolean);
    // Tidak ada gambar → JANGAN preventDefault, biarkan paste teks normal.
    if (!imgs.length) return;
    e.preventDefault();
    addImages(imgs);
  }, [enabled, addImages]);

  const onDragOver = useCallback((e) => {
    if (!enabled) return;
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    // WAJIB: tanpa preventDefault di dragover, event drop tidak pernah
    // dipancarkan dan browser malah membuka file-nya sebagai navigasi.
    e.preventDefault();
    setImgOver(true);
  }, [enabled]);

  const onDragLeave = useCallback(() => setImgOver(false), []);

  const onDrop = useCallback((e) => {
    setImgOver(false);
    if (!enabled) return;
    const files = Array.from(e.dataTransfer?.files || []).filter(f => /^image\//i.test(f.type));
    if (!files.length) return;   // drop non-gambar (mis. teks) biarkan apa adanya
    e.preventDefault();
    addImages(files);
  }, [enabled, addImages]);

  // Bentuk siap-kirim { format, b64 } untuk /api/ai/ask.
  const toImageParts = useCallback(
    () => (enabled ? attachedImgs.map(a => dataUriToImagePart(a.uri)).filter(Boolean) : []),
    [enabled, attachedImgs],
  );

  // [THUMB] Bentuk ringan untuk transkrip & riwayat: { thumb, name, bytes }.
  // `uri` penuh SENGAJA tidak ikut — itu yang membedakan jejak riwayat (kecil,
  // permanen) dari muatan yang dikirim ke model (besar, sekali pakai).
  // Lampiran yang miniaturnya gagal dibuat TETAP dicatat namanya, supaya user
  // tahu ada gambar walau tak bisa ditampilkan.
  const toImageMarks = useCallback(
    () => (enabled ? attachedImgs.map(a => ({
      thumb:   a.thumb   || '',
      preview: a.preview || '',
      name:    a.name    || '',
      bytes:   a.bytes   || 0,
    })) : []),
    [enabled, attachedImgs],
  );

  return {
    attachedImgs, imgBusy, imgOver, imgInputRef,
    addImages, removeImage, clearImages, openPicker,
    onFileSelected, onPaste, onDragOver, onDragLeave, onDrop,
    toImageParts, toImageMarks,
    atMax: attachedImgs.length >= CHAT_IMG_MAX,
  };
}
